/**
 * CO LEŻY NA DYSKU MASZYNY — SPIS AGENTA UZUPEŁNIONY O TO, CO PORTAL SAM TAM POŁOŻYŁ.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z ZAMKNIĘTEGO KOŁA ZMIERZONEGO NA CHMUROWYM MASTERZE (25.08.2026), i to jest
 *  dokładnie ta sama pułapka, co przy `connectSerwer` — tylko o jedno piętro wyżej.
 *
 *  Spis kopii jedzie W SYGNALE ŻYCIA UDANEGO CYKLU. Cykl agenta na świeżym masterze udany nie
 *  jest NIGDY: bazy `pesa` jeszcze nie ma, więc połączenie się nie udaje i pętla kończy przebieg
 *  wyjątkiem. Skutek zmierzony w portalu: `KOPIA_POBIERZ` kończy się powodzeniem w 0,6 s
 *  (`output.lokalnie = /kopie/lokalne/pesa_…bak.zip`), a ekran kopii pisze „ten agent nie prowadzi
 *  kopii na dysku" — czyli przycisk „odtwórz" nie ma przy czym się pojawić. Odtworzenie tej paczki
 *  jest jedyną drogą, żeby ta baza powstała, a więc żeby cykl kiedykolwiek się udał.
 *
 *  ⚠️ POPRAWKA JEST TU, A NIE TYLKO W AGENCIE, Z POWODU DOSTAWY. Agent w chmurze przyjeżdża
 *  w obrazie platformy, a agenci u klientów aktualizują się dopiero po UDANYM cyklu — czyli
 *  poprawka po stronie agenta nie dociera właśnie tam, gdzie jest potrzebna. Portal wdraża się
 *  sam, więc to on musi umieć pokazać paczkę, o której WIE Z WŁASNEGO ZADANIA.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Pozycja spisu — ten sam kształt, w jakim przysyła go agent (`localBackups`). */
export type PaczkaNaDysku = { nazwa: string; rozmiar: number; utworzono: string; suma: string };

/** Tyle z zadania maszynowego, ile trzeba do rozstrzygnięcia — bez ciągnięcia całego typu. */
export type ZadanieDoSpisu = {
  kind: string;
  status: string;
  createdAt: string;
  finishedAt: string | null;
  output: unknown;
  agent: { id: string };
};

const czas = (iso: string | null | undefined): number => (iso == null ? 0 : new Date(iso).getTime());

/**
 * Spis do pokazania na ekranie i zbiór sum tych pozycji, które wiemy Z ZADANIA, a nie ze spisu.
 *
 * ⚠️ `null` ZNACZY „AGENT NIE ZMIERZYŁ", i ta różnica musi przeżyć — ekran rozróżnia trzy „nic"
 * (nie prowadzi kopii / nie zdążył zmierzyć / zmierzył i pusto), a każde wymaga innej reakcji.
 */
export function paczkiNaDysku(opts: {
  /** `localBackups` agenta: `null`/`undefined` = nigdy nie przysłał spisu. */
  spis: readonly PaczkaNaDysku[] | null | undefined;
  /** `diskAt` agenta — kiedy powstał spis. */
  spisZ: string | null | undefined;
  zadania: readonly ZadanieDoSpisu[];
  agentId: string;
}): { lista: PaczkaNaDysku[] | null; zZadan: ReadonlySet<string> } {
  const spis = opts.spis ?? null;
  const znane = new Set((spis ?? []).map((k) => k.suma));
  const odKiedySpis = czas(opts.spisZ);

  const dolozone = new Map<string, PaczkaNaDysku>();
  for (const z of opts.zadania) {
    /**
     * ⚠️ WYŁĄCZNIE `KOPIA_POBIERZ`, i to nie jest przeoczenie. To jedyne zadanie, po którym portal
     * zna KOMPLET danych pozycji spisu — nazwę, rozmiar i SUMĘ (`output.suma`), a suma jest tu
     * kluczem wiersza i rozstrzyga o wysyłce z powrotem do magazynu. `KOPIA_LOKALNA` sumy nie
     * zwraca, a poza tym wymaga działającej bazy — czyli udanego cyklu, czyli spis i tak przyjdzie.
     */
    if (z.kind !== 'KOPIA_POBIERZ' || z.status !== 'DONE' || z.agent.id !== opts.agentId) continue;
    const o = z.output as { nazwa?: unknown; suma?: unknown; size?: unknown } | null;
    if (typeof o?.nazwa !== 'string' || typeof o.suma !== 'string' || typeof o.size !== 'number') continue;
    if (znane.has(o.suma)) continue;

    /**
     * ⚠️⚠️ TYLKO TO, CZEGO SPIS NIE MÓGŁ WIDZIEĆ — I TEN WARUNEK JEST CAŁYM BEZPIECZNIKIEM.
     *
     * Bez niego dołożona pozycja zostawałaby na ekranie NA ZAWSZE, także po skasowaniu pliku:
     * odtworzenie w buforze sidecara USUWA paczkę po udanym `RESTORE`, więc pierwszy udany spis
     * po zasianiu mastera przyszedłby pusty, a my dorysowalibyśmy do niego plik, którego już nie
     * ma — z czynnym przyciskiem „odtwórz". Porównanie z chwilą pomiaru sprawia, że uzupełnienie
     * SAMO SIĘ WYŁĄCZA w chwili, gdy agent zaczyna mówić o katalogu prawdę.
     */
    const kiedy = z.finishedAt ?? z.createdAt;
    if (odKiedySpis !== 0 && odKiedySpis >= czas(kiedy)) continue;

    dolozone.set(o.suma, { nazwa: o.nazwa, rozmiar: o.size, utworzono: kiedy, suma: o.suma });
  }

  if (spis === null && dolozone.size === 0) return { lista: null, zZadan: new Set() };

  /** Najnowsze na górze — tak samo jak lista kopii w magazynie. */
  const lista = [...(spis ?? []), ...dolozone.values()].sort((a, b) => czas(b.utworzono) - czas(a.utworzono));
  return { lista, zZadan: new Set(dolozone.keys()) };
}
